import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { Header } from '@components/Header';
import { PagePagination } from '@components/core-ui/Pagination';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { Tabs } from '@pages/Admin/AttorneysAccessPage/components/Tabs';
import { useAPI } from '@providers/useAPI';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { PaginatedAttorneyPasscodeResponseDto } from 'app/API';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { useListQueryParams } from 'app/hooks/useListQueryParams';
import { attorneysKeys, TAttorneysListParams } from 'app/queries/attorneys/attorneysKeys';
import { useMemo } from 'react';
import { createFetchAttorneyPasscodesQueryFn } from '../../api/fetchAttorneyPasscodes';
import { CellContent } from './styles/CellContent';
import { Wrapper } from './styles/Wrapper';

const SEARCH_DEBOUNCE_MS = 300;

export const Content = () => {
  const { call } = useAPI();
  const { page, pageSize, search, orderBy, orderType, setSearch, setPage, setPageSize } = useListQueryParams({
    defaults: { pageSize: 25, orderBy: 'createdAt', orderType: 'desc' },
  });
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const isSearchSettling = search !== debouncedSearch;
  const listParams = useMemo<TAttorneysListParams>(
    () => ({ page, pageSize, orderBy, orderType, search: debouncedSearch }),
    [debouncedSearch, orderBy, orderType, page, pageSize],
  );

  const passcodesQuery = useQuery({
    queryKey: attorneysKeys.passcodeList(listParams),
    queryFn: createFetchAttorneyPasscodesQueryFn(call),
    placeholderData: keepPreviousData,
    enabled: !isSearchSettling,
  });

  const attorneyPasscodesList = passcodesQuery.data ?? { data: [], count: 0 };

  return (
    <>
      <Header
        title="Attorney Passcodes"
        setSearch={setSearch}
        search={search}
        leftSideContent={[<Tabs key="PageTabs" currentTab="Passcodes" />]}
      />

      <LinearProgressLoader active={passcodesQuery.isFetching || isSearchSettling} />
      <Wrapper>
        <TableWrapper>
          <Table>
            <Thead>
              <Tr>
                <Th>#</Th>
                <Th>Passcode</Th>
                <Th>Recording session id</Th>
                <Th>Job id</Th>
                <Th>Created</Th>
              </Tr>
            </Thead>
            <Tbody>
              {attorneyPasscodesList.data?.map(
                (data: PaginatedAttorneyPasscodeResponseDto['data'][0], index: number) => (
                  <Tr key={data.id ?? index}>
                    <Td>
                      <CellContent>{index + 1}</CellContent>
                    </Td>
                    <Td>
                      <CellContent>{data.passcode}</CellContent>
                    </Td>
                    <Td>
                      <CellContent>{data.recordingSessionId}</CellContent>
                    </Td>
                    <Td>
                      <CellContent>{data.RecordingSession?.jobId}</CellContent>
                    </Td>
                    <Td>
                      <CellContent>{getAmericanTime(data.createdAt)}</CellContent>
                    </Td>
                  </Tr>
                ),
              )}
            </Tbody>
          </Table>
        </TableWrapper>
        <PagePagination
          totalItemsCount={attorneyPasscodesList?.count ?? 0}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </Wrapper>
    </>
  );
};
